/**
 * Display-only helpers for purchase payout / escrow UI.
 *
 * These functions READ fields persisted by backend settlement, webhooks, and
 * release paths (functions/payoutCalc.js, stripeWebhook.js, etc.). They do
 * NOT compute fees, net payout, or any amount — zero calculation logic here.
 *
 * Stripe transfer amount at release time is determined server-side by
 * effectivePayoutCents() in functions/payoutCalc.js. The read order below
 * mirrors that function for display purposes only.
 */

/** Agreed purchase price shown to buyer/seller (not the Stripe transfer amount). */
export const getPurchasePrice = (offer) =>
  offer?.acceptedAmount ?? offer?.offerAmount ?? null;

/**
 * Cents the seller receives at escrow release, as stored on the offer doc.
 *
 * 1. payoutAdjustment.expectedNetCents — verbatim when present (authoritative
 *    post-F1, written by settlement / webhook).
 * 2. transferStatus.escrowAmount — verbatim fallback for legacy offers that
 *    predate payoutAdjustment (no frontend priority reimplementation beyond
 *    reading whichever field the backend populated).
 *
 * Returns null when neither field is a finite number.
 */
export const getSellerPayoutCents = (offer) => {
  const expectedNetCents = offer?.payoutAdjustment?.expectedNetCents;
  if (typeof expectedNetCents === 'number' && Number.isFinite(expectedNetCents)) {
    return expectedNetCents;
  }

  const escrowAmount = offer?.transferStatus?.escrowAmount;
  if (typeof escrowAmount === 'number' && Number.isFinite(escrowAmount)) {
    return escrowAmount;
  }

  return null;
};

/**
 * Zero-net escrow hold — backend-set only (stripeWebhook, repairFailedEscrow).
 * Purchases with zeroNetPayout still require delivery confirmation before close-out.
 * Do NOT infer zero-net from payout amounts in the UI; dispute/refund uses
 * disputeHold separately.
 *
 * STALE-FLAG OVERRIDE: the webhook freezes zeroNetPayout at the BUYER's
 * checkout, before the seller settles. A referral-credit (or card-paying)
 * seller can then raise the true net above zero via payoutAdjustment — the
 * release functions already pay effectivePayoutCents so the money is right,
 * but this banner told a seller who WAS getting paid "no cash payout"
 * (found during Android testing, 2026-08-20: $3 sale, fee waived by credit).
 * Settlement's expectedNetCents is authoritative here for the same reason it
 * is in payoutCalc.js.
 */
export const isZeroNetEscrowHold = (offer) => {
  if (offer?.transferStatus?.zeroNetPayout !== true) return false;
  const adjustedNet = offer?.payoutAdjustment?.expectedNetCents;
  if (typeof adjustedNet === 'number' && Number.isFinite(adjustedNet) && adjustedNet > 0) {
    return false;
  }
  return true;
};

export const formatPurchaseTransferStatus = (offer) => {
  const ts = offer?.transferStatus;
  if (!ts) return null;

  if (ts.status === 'success') return 'Transfer Successful';
  if (ts.status === 'failed') return 'Transfer Failed';
  if (ts.status === 'processing') return 'Processing';

  if (ts.status === 'held_in_escrow') {
    if (isZeroNetEscrowHold(offer)) {
      return 'Awaiting delivery confirmation';
    }
    const payoutCents = getSellerPayoutCents(offer);
    if (payoutCents != null && payoutCents > 0) {
      return `Held in Escrow ($${(payoutCents / 100).toFixed(2)})`;
    }
    return 'Held in Escrow';
  }

  return 'Processing';
};
